import type {
    ContentBlock} from "@@/shared/acquire/content-block";
import {
    contentBlockZodSchema,
} from "@@/shared/acquire/content-block";
import type {
    ExtrasGroup} from "@@/shared/acquire/extras-group";
import {
    extrasGroupZodSchema,
} from "@@/shared/acquire/extras-group";
import type {
    RReferences} from "@@/shared/acquire/resource-references/resource-reference-group";
import {
    rReferencesZodSchema,
} from "@@/shared/acquire/resource-references/resource-reference-group";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    DurationInMinutes} from "@@/shared/date-time/time-spans";
import {
    durationInMinutesZodSchema,
} from "@@/shared/date-time/time-spans";
import { idZodSchema } from "@@/shared/id";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ProductId} from "../product-id";
import { productIdZodSchema } from "../product-id";
import type { ProductCommandCommon } from "./product-commands-common";

type BookableEditCommand = ProductCommandCommon & {
    readonly _id: string;
    readonly _type: "BOOKABLE_EDIT_COMMAND";

    readonly extras: ProductId[];
    readonly extrasGroups?: ExtrasGroup[];
    readonly mainResource: RReferences;
    readonly resources: RReferences[];
    readonly contentBlocks: ContentBlock[];

    //Tablebooking
    /**
     * The amount of people that can be booked at the same time to not overflow the kitchen
     */
    readonly maxGuestsPerSession?: number;
    /**
     * Are customers allowed to cancel their reservation themselves using the app without contacting the provider
     * @type {boolean}
     */
    readonly cancellationByCustomerAllowed?: boolean;
    /**
     * How far ahead can a customer cancel a table reservation themselves
     * @type {DurationInMinutes}
     */
    readonly cancellationInAdvanceTimeLimit?: DurationInMinutes;

    /**
     * Limit the end date of the rental to match with another session
     */
    readonly spanning?: boolean;

    /**
     * Force a specific duration for the rental
     */
    readonly rentBy?: "HOUR" | "DAY" | "NIGHT";
};

type FactoryParams = Except<
    ProductCommandCommon & BookableEditCommand,
    "_id" | "_type" | "_createdAt"
>;
const bookableEditCommandFactory = (params: FactoryParams) => {
    const editCommand: BookableEditCommand = {
        ...params,
        _id: generateId({ prefix: "bookable_edit_command__" }),
        _type: "BOOKABLE_EDIT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return editCommand;
};

const bookableEditCommandZodSchema: z.ZodSchema<BookableEditCommand> = z.object(
    {
        _id: idZodSchema,
        _type: z.literal("BOOKABLE_EDIT_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        providerId: providerIdZodSchema,
        productId: productIdZodSchema,
        extras: z.array(productIdZodSchema),
        extrasGroups: z.array(extrasGroupZodSchema).optional(),
        mainResource: rReferencesZodSchema,
        resources: z.array(rReferencesZodSchema),
        contentBlocks: z.array(contentBlockZodSchema),

        maxGuestsPerSession: z.number().int().optional(),
        cancellationByCustomerAllowed: z.boolean().optional(),
        cancellationInAdvanceTimeLimit: durationInMinutesZodSchema.optional(),
        spanning: z.boolean().optional(),
        rentBy: z.optional(z.enum(["HOUR", "DAY", "NIGHT"])),
    },
);

export { bookableEditCommandFactory, bookableEditCommandZodSchema };
export type { BookableEditCommand, FactoryParams as BookableEditCommandParams };
